import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Observable } from 'rxjs';
import { JWTTokenServiceService } from '../public/services/token.service';
import { PermissionService } from '../private/services/permission.service';
import { LocalStorageService } from './LocalStorageService';

function roleAllows(
  permissionService: PermissionService,
  module: string,
  parents: string[],
  allowParentModule: boolean,
): boolean {
  return permissionService.hasNavAccess(module, parents, allowParentModule);
}

export const AuthGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
):
  | Observable<boolean | UrlTree>
  | Promise<boolean | UrlTree>
  | boolean
  | UrlTree => {
  const router = inject(Router);
  const tokenService = inject(JWTTokenServiceService);
  const permissionService = inject(PermissionService);
  const localStorageService = inject(LocalStorageService);

  const routeName = route.routeConfig?.path || '';

  /* ============================================
     1️⃣ AUTHENTICATION CHECK
  ============================================ */

  if (!tokenService.getToken() || tokenService.isTokenExpired()) {
    console.log('AuthGuard: Not authenticated');
    return router.createUrlTree(['login']);
  }

  // Prevent login access if already authenticated
  if (routeName === 'login') {
    return router.createUrlTree(['main/leads']);
  }

  /* ============================================
     2️⃣ PERMISSION CHECK
  ============================================ */

  const feature = route.data['feature'];
  const parents = (route.data['permissionParents'] as string[] | undefined) || [];
  const allowParentModule = route.data['allowParentModule'] === true;
  const queryParam = route.data['permissionQueryParam'] as string | undefined;
  const queryMap = route.data['permissionQueryMap'] as
    | Record<string, string>
    | undefined;
  const anyOf = (route.data['permissionAnyOf'] as string[] | undefined) || [];

  let module = route.data['module'] as string | undefined;
  let checkAny = false;

  if (queryParam && queryMap) {
    const value = route.queryParamMap.get(queryParam);
    if (value && queryMap[value]) {
      module = queryMap[value];
    } else if (anyOf.length) {
      checkAny = true;
      module = undefined;
    }
  }

  console.log('module', module);
  console.log('feature', feature);

  // If no module specified → allow authenticated route
  if (!module && !checkAny) {
    return true;
  }

  // Special legacy override (optional)
  const clientId = localStorageService.getClientId();
  if (clientId == 70 && module === 'sales-mail-hub') {
    return true;
  }

  // Ensure permissions are loaded
  permissionService.refresh();

  /* ===== MODULE CHECK ===== */

  console.log('AuthGuard: Module → ', module);

  const hasModuleAccess = checkAny
    ? anyOf.some((key) =>
        roleAllows(permissionService, key, parents, allowParentModule),
      )
    : roleAllows(
        permissionService,
        module as string,
        parents,
        allowParentModule,
      );

  if (!hasModuleAccess) {
    console.warn(`AuthGuard: Module denied → ${module || anyOf.join(', ')}`);
    return router.createUrlTree(['unauthorized']);
  }

  /* ===== FEATURE CHECK ===== */

  if (feature && module) {
    const hasFeatureAccess = permissionService.hasFeatureAccess(
      module,
      feature,
    );

    if (!hasFeatureAccess) {
      console.warn(`AuthGuard: Feature denied → ${module}.${feature}`);
      return router.createUrlTree(['unauthorized']);
    }
  }

  /* ============================================
     3️⃣ ACCESS GRANTED
  ============================================ */

  console.log(
    `AuthGuard: Access granted → ${module}${feature ? '.' + feature : ''}`,
  );

  return true;
};
